// frontend/src/components/wizard/StepReview/index.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { ArrowLeft, Play, Loader2, AlertCircle, Beaker } from 'lucide-react';
import { useWizardStore } from '../../../store/useWizardStore';
import { useLanguageStore } from '../../../store/useLanguageStore';
import { apiClient } from '../../../api/client';
import { Modal } from '../../Modal';

import { DataFlowSummary } from './DataFlowSummary';
import { MappingSummary } from './MappingSummary';
import { NotificationPanel } from './NotificationPanel';
import { ScheduleSettings } from './ScheduleSettings';
import { SyncModePanel } from './SyncModePanel';
import { DangerZone } from './DangerZone';
import { DryRunModal } from './DryRunModal';
import { FilterConfigPanel } from './FilterConfigPanel';
import { DataLineagePanel } from '../../DataLineage';
import { QualityRulesSummary } from './QualityRulesSummary';

export default function StepReview() {
  const {
    isActive, setIsActive,
    runMode, setRunMode,
    source, target, mapping,
    getPayload, prevStep,
    pipelineStatus, setPipelineStatus,
    editId, scheduleUI,
    notifications, setNotifications,
    dedupConfig,
    filterConfig, setFilterConfig,   // 🆕 filtro delta
    sourceColumns,                    // 🆕 columnas origen (ya existe en el store)
    sourceSelectedTables,
    targetSelectedTables,
  } = useWizardStore();
  const { t } = useLanguageStore();

  const [isLoading, setIsLoading] = useState(false);
  const [dryRunLimit, setDryRunLimit] = useState(50);
  const [modalState, setModalState] = useState({
    isOpen: false, title: '', message: '', showCancel: false, onAccept: null,
  });
  const [dryRun, setDryRun] = useState({
    isOpen: false, isLoading: false, data: null, error: null, activeTab: 'transformed',
  });

  // ── Scroll to top al entrar al paso de revisión ────────────────
  useEffect(() => { window.scrollTo({ top: 0, behavior: 'instant' }); }, []);

  // ── Helper para abrir el modal estándar ──────────────────────────
  const openModal = (title, message, { showCancel = false, onAccept = null } = {}) => {
    setModalState({
      isOpen: true, title, message, showCancel,
      onAccept: onAccept ?? (() => setModalState(prev => ({ ...prev, isOpen: false }))),
    });
  };

  // ── Métricas del mapping ──────────────────────────────────────────
  const mappedFields = mapping?.filter(m => m.type && m.type !== 'unmapped' && m.type !== 'ignore').length || 0;
  const ignoredFields = mapping?.filter(m => m.type === 'ignore').length || 0;
  const fixedFields = mapping?.reduce((acc, m) => acc + (m.pipeline?.some(r => r.id === 'SET_FIXED') ? 1 : 0), 0) || 0;
  const totalRules = mapping?.reduce((acc, m) => acc + (m.pipeline?.length || 0), 0) || 0;

  // ── Sprint 13.1: Preparar mappings para DataLineagePanel ──────
  const lineageMappings = useMemo(() => {
    return (mapping || []).map(m => ({
      sourceField: m.source || '',
      targetField: m.target || '',
      transformations: (m.pipeline || []).map(r => ({
        type: r.id || r.type || r.ruleType || 'UNKNOWN',
        ruleConfig: r.ruleConfig || r.config || {},
      })),
      ignored: m.type === 'unmapped' || m.type === 'ignore',
      isFixed: m.type === 'fixed' || m.pipeline?.some(r => r.id === 'SET_FIXED'),
      fixedValue: m.pipeline?.find(r => r.id === 'SET_FIXED')?.ruleConfig?.value ?? null,
    }));
  }, [mapping]);

  const wizardMode = useMemo(() => {
    const nSrc = sourceSelectedTables?.length || 1;
    const nTgt = targetSelectedTables?.length || 1;
    if (nSrc > 1 && nTgt > 1) return 'N:M';
    if (nSrc > 1) return 'N:1';
    if (nTgt > 1) return '1:N';
    return '1:1';
  }, [sourceSelectedTables, targetSelectedTables]);

  const MAX_EMAILS = 15;
  const currentTotalEmails = (notifications?.to?.length || 0) + (notifications?.cc?.length || 0);

  const [qualityPreview, setQualityPreview] = useState([]);

  // ── Auto-completar email del usuario logueado ─────────────────────
  // Solo para nuevas migraciones sin destinatario previo.
  useEffect(() => {
    if (editId || (notifications?.to?.length || 0) > 0) return;

    try {
      let userEmail = '';
      // 1. Buscar en localStorage (múltiples claves posibles)
      for (const key of ['user', 'userInfo', 'vnext_user', 'auth', 'session']) {
        const raw = localStorage.getItem(key);
        if (raw) {
          const parsed = JSON.parse(raw);
          userEmail = parsed?.email || parsed?.user?.email || '';
          if (userEmail) break;
        }
      }
      // 2. Fallback: decodificar JWT
      if (!userEmail) {
        const token = localStorage.getItem('vnext_auth_token');
        if (token) {
          const payload = JSON.parse(
            decodeURIComponent(
              window.atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))
                .split('')
                .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                .join('')
            )
          );
          userEmail = payload?.email || payload?.sub || '';
        }
      }
      if (userEmail) setNotifications({ ...notifications, to: [userEmail] });
    } catch (err) {
      console.error('No se pudo auto-completar el email del usuario:', err);
    }
  }, []);

  // ── Lanzar migración ──────────────────────────────────────────────
  const handleLaunchMigration = async () => {
    // 🔧 Validar dedup: si está activado exige columna de referencia
    if (dedupConfig?.enabled && !dedupConfig?.column) {
      openModal(
        t('wizard.review.main.dedup_missing_title') || '⚠️ Columna de Dedup Requerida',
        t('wizard.review.main.dedup_missing_desc') ||
        'Activaste "Evitar duplicados" pero no seleccionaste una columna de referencia. ' +
        'Regresa a la etapa de Mapping para completar la configuración.'
      );
      return;
    }

    if (currentTotalEmails === 0) {
      openModal(
        t('wizard.review.main.missing_emails_title') || '⚠️ Faltan Destinatarios',
        t('wizard.review.main.missing_emails_msg') || 'Añade al menos un correo destinatario antes de guardar.'
      );
      return;
    }

    setIsLoading(true);
    const payload = getPayload();
    payload.frequencyType = runMode;
    payload.runMode = runMode;
    payload.parameters = payload.parameters || {};
    payload.parameters.notifications = notifications;

    try {
      const response = await apiClient.post('/onboarding/migrations/launch', payload);

      if (response.data.success) {
        const savedId = response.data.idConfigHead;
        let executed = !!response.data.taskId;

        // Pipeline existente + "Una sola vez" + activo → forzar ejecución
        if (!executed && editId && runMode === 'once' && isActive) {
          try {
            await apiClient.post(`/dashboard/pipelines/${savedId}/execute`);
            executed = true;
          } catch (execErr) {
            console.error('Error forzando ejecución:', execErr);
          }
        }

        openModal(
          executed
            ? (t('wizard.review.main.sent_title') || '🚀 Proceso Enviado')
            : (t('wizard.review.main.saved_title') || '✅ Guardado'),
          executed
            ? (t('wizard.review.main.sent_msg') || 'La migración fue enviada. Puedes ver el resultado en el Dashboard.')
            : (t('wizard.review.main.saved_msg') || 'La configuración fue guardada correctamente.'),
          { onAccept: () => { window.location.href = '/dashboard'; } }
        );
      }
    } catch (error) {
      if (!error._quotaHandled) {
        openModal(
          t('common.error') || 'Error',
          error.message || t('wizard.review.main.error_critical') || 'Ocurrió un error inesperado.'
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  // ── Dry Run (preview de transformaciones) ─────────────────────────
  const handleDryRun = async () => {
    setDryRun({ isOpen: true, isLoading: true, data: null, error: null, activeTab: 'transformed' });
    try {
      const payload = getPayload();
      payload.parameters = payload.parameters || {};
      payload.parameters.notifications = notifications;

      const res = await apiClient.post(`/onboarding/migrations/dry-run?limit=${dryRunLimit}`, payload);
      if (res.data.success) {
        setDryRun(prev => ({ ...prev, isLoading: false, data: res.data.data }));
        setQualityPreview(res.data?.data?.quality || []);
      }
    } catch (error) {
      setDryRun(prev => ({
        ...prev,
        isLoading: false,
        error: error.message || t('wizard.review.main.error_sim') || 'Falló la simulación.',
      }));
    }
  };

  // ── Simulate DB (dry-run con ROLLBACK en BD real) ─────────────────
  const handleSimulateDB = async () => {
    setDryRun(prev => ({ ...prev, isLoading: true, error: null }));
    try {
      const payload = getPayload();
      payload.parameters = payload.parameters || {};
      payload.parameters.notifications = notifications;

      const res = await apiClient.post('/onboarding/migrations/dry-run?limit=5&simulate_db=true', payload);
      if (res.data.success) {
        setDryRun(prev => ({ ...prev, isLoading: false, isOpen: false }));
        setTimeout(() => {
          openModal(
            t('wizard.review.main.success_title') || '✅ Éxito',
            t('wizard.review.dry_run.sim_db_success_msg') ||
            'Simulación en Base de Datos exitosa. Los constraints son correctos.'
          );
        }, 300);
      }
    } catch (error) {
      setDryRun(prev => ({
        ...prev,
        isLoading: false,
        activeTab: 'original',
        error: error.message || t('wizard.review.main.error_dest') || 'Error al conectar con el destino.',
      }));
    }
  };

  // ── Anular / Reactivar pipeline ───────────────────────────────────
  const handleAnnulClick = () => {
    openModal(
      t('wizard.review.main.annul_title') || 'Anular Configuración',
      t('wizard.review.main.annul_msg') || 'Al anular, el pipeline dejará de ejecutarse manual y automáticamente.',
      {
        showCancel: true,
        onAccept: () => { setPipelineStatus(0); setIsActive(false); setModalState(prev => ({ ...prev, isOpen: false })); },
      }
    );
  };

  const handleReactivateClick = () => {
    openModal(
      t('wizard.review.main.reactivate_title') || 'Reactivar Configuración',
      t('wizard.review.main.reactivate_msg') || 'El pipeline se restaurará en estado "Pausado" por seguridad.',
      {
        showCancel: true,
        onAccept: () => {
          setPipelineStatus(2);
          setIsActive(false);
          if (scheduleUI) setRunMode('scheduled');
          setModalState(prev => ({ ...prev, isOpen: false }));
        },
      }
    );
  };

  // ── Texto y color del botón principal ────────────────────────────
  let btnText = t('wizard.review.main.btn_draft') || 'Guardar Borrador (Inactivo)';
  let btnColor = 'bg-slate-700 hover:bg-slate-800';
  if (isActive) {
    if (runMode === 'once') {
      btnText = t('wizard.review.main.btn_execute') || 'Guardar y Ejecutar';
      btnColor = 'bg-emerald-600 hover:bg-emerald-700';
    } else {
      btnText = t('wizard.review.main.btn_schedule') || 'Guardar y Programar';
      btnColor = 'bg-indigo-600 hover:bg-indigo-700';
    }
  }

  return (
    <div className="w-full max-w-6xl mx-auto pb-12 relative animate-in fade-in">

      {/* Encabezado + Botón Simular ETL */}
      <div className="mb-8 flex flex-col sm:flex-row justify-between sm:items-end gap-4">
        <div>
          <h2 className="text-3xl font-bold text-slate-900">
            {t('wizard.review.main.title') || 'Revisión Final'}
          </h2>
          <p className="text-slate-500 mt-2">
            {t('wizard.review.main.desc') || 'Verifica la configuración antes de guardar.'}
          </p>
        </div>

        <div className={`flex items-center gap-2 bg-blue-50/50 p-1.5 rounded-xl border border-blue-100 transition-all shadow-sm w-full sm:w-auto ${pipelineStatus === 0 ? 'opacity-50 pointer-events-none grayscale' : ''}`}>
          <div className="flex items-center gap-1.5 pl-1">
            <select
              value={dryRunLimit}
              onChange={e => setDryRunLimit(Number(e.target.value))}
              disabled={pipelineStatus === 0}
              className="bg-white border border-blue-200 text-blue-700 px-2 sm:px-3 py-2 rounded-lg text-xs sm:text-sm font-bold outline-none cursor-pointer focus:ring-2 focus:ring-blue-500 shrink-0"
            >
              <option value={10}>10</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={500}>500</option>
            </select>
            <span className="hidden sm:inline text-blue-700 text-sm font-bold whitespace-nowrap">
              {t('wizard.review.main.rows') || 'Filas'}
            </span>
          </div>
          <button
            onClick={handleDryRun}
            disabled={pipelineStatus === 0}
            className="bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs sm:text-sm px-4 py-2.5 rounded-lg flex-1 sm:flex-none flex items-center justify-center transition shadow-sm disabled:opacity-50 whitespace-nowrap"
          >
            <Beaker className="w-4 h-4 mr-2 shrink-0" />
            {t('wizard.review.main.sim_etl') || 'Simular ETL'}
          </button>
        </div>
      </div>

      {/* Layout principal */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">

        {/* Columna izquierda */}
        <div className="lg:col-span-7 space-y-6">
          <DataFlowSummary source={source} target={target} />

          {/* SyncModePanel: visible Y editable en StepReview
              Motivo: cambiar syncMode = 0 llamadas al backend, pure Zustand.
              Evita que el usuario tenga que volver a conectar origen y destino
              solo para cambiar append → upsert.
              Mismo precedente que ScheduleSettings (también editable aquí). */}
          <SyncModePanel />

          <MappingSummary
            mappedFields={mappedFields}
            ignoredFields={ignoredFields}
            fixedFields={fixedFields}
            totalRules={totalRules}
          />

          {/* 🧬 Sprint 13.1: Data Lineage Visual */}
          <DataLineagePanel
            mappings={lineageMappings}
            sourceEngine={(source?.type || '').toUpperCase()}
            targetEngine={(target?.type || '').toUpperCase()}
            mode={wizardMode}
          />

          {/* 📏 Sprint 14: Data Rules — resumen de calidad */}
          <QualityRulesSummary mappings={mapping} />

          {/* BUG FIX: se pasa onLimitReached para reemplazar alert() en EmailChipInput */}
          <NotificationPanel
            notifications={notifications}
            setNotifications={setNotifications}
            MAX_EMAILS={MAX_EMAILS}
            currentTotalEmails={currentTotalEmails}
            onLimitReached={(max) => openModal(
              t('wizard.review.notifications.limit_title') || 'Límite de destinatarios',
              t('wizard.review.notifications.limit_reached') || `No puedes agregar más de ${max} destinatarios en total.`
            )}
          />
          <FilterConfigPanel
            filterConfig={filterConfig}
            onChange={(patch) => setFilterConfig(patch)}
            sourceEngine={source?.type || ''}
            sourceColumns={sourceColumns || []}
          />
        </div>

        {/* Columna derecha */}
        <div className="lg:col-span-5 space-y-6">
          <ScheduleSettings />
          <DangerZone
            editId={editId}
            pipelineStatus={pipelineStatus}
            handleAnnulClick={handleAnnulClick}
            handleReactivate={handleReactivateClick}
          />
        </div>
      </div>

      {/* Barra de acción final */}
      <div className="flex justify-between items-start mt-8">
        <button
          onClick={prevStep}
          className="px-6 py-2.5 text-slate-600 hover:bg-slate-100 rounded-xl flex items-center"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          {t('common.buttons.back') || 'Atrás'}
        </button>

        <div className="flex flex-col items-end">
          {currentTotalEmails === 0 && (
            <div className="mb-4 p-3 bg-red-50 border-2 border-red-400 rounded-lg text-center animate-pulse shadow-sm max-w-sm">
              <p className="text-sm font-black text-red-600 flex items-center justify-center">
                <AlertCircle className="w-5 h-5 mr-2" />
                {t('wizard.review.main.alert_missing_email') || '¡FALTA CORREO DESTINATARIO!'}
              </p>
              <p className="text-red-500 text-xs font-bold mt-1">
                {t('wizard.review.main.alert_missing_email_desc') || 'Añade un correo en la sección superior para poder guardar.'}
              </p>
            </div>
          )}

          <button
            onClick={() => {
              if (currentTotalEmails === 0) {
                openModal(
                  t('wizard.review.main.missing_emails_title') || '⚠️ Faltan Destinatarios',
                  t('wizard.review.main.alert_missing_email_desc') || 'Debes incluir al menos un correo destinatario.'
                );
                return;
              }
              handleLaunchMigration();
            }}
            disabled={isLoading || !mapping?.length || currentTotalEmails === 0}
            className={`px-8 py-2.5 text-white font-bold rounded-xl flex items-center transition shadow-md disabled:opacity-50 disabled:cursor-not-allowed ${btnColor}`}
          >
            {isLoading
              ? <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              : <Play className="w-5 h-5 mr-2" />
            }
            {btnText}
          </button>

          <p className="text-[11px] font-bold text-amber-700 mt-2 flex items-center bg-amber-50 px-3 py-1.5 rounded-full border border-amber-200 shadow-sm">
            <AlertCircle className="w-3.5 h-3.5 mr-1.5 text-amber-600" />
            {t('wizard.review.main.confirm_changes') || 'Guarde o ejecute para confirmar los cambios'}
          </p>
        </div>
      </div>

      {/* Modales */}
      <DryRunModal
        dryRun={dryRun}
        setDryRun={setDryRun}
        dryRunLimit={dryRunLimit}
        target={target}
        mapping={mapping}
        handleSimulateDB={handleSimulateDB}
        qualityData={qualityPreview}
      />
      <Modal
        isOpen={modalState.isOpen}
        title={modalState.title}
        message={modalState.message}
        onClose={() => setModalState(prev => ({ ...prev, isOpen: false }))}
        onAccept={modalState.onAccept}
        showCancel={modalState.showCancel}
      />
    </div>
  );
}
